import {useMemo, useState, type ReactNode} from 'react';
import {Plus, Trash2, Pencil, Check, X, AlertCircle, Search} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NativeSelect,
  NativeSelectOption,
  Spinner,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {getApiErrorMessage} from '@/api/client';
import {useConfirm} from '@/components/shared/useConfirm';
import {type EscalationContactRule, type EscalationResolveResult} from '@/api/escalation';
import {type P0SlackChannel} from '@/api/p0SlackChannels';
import {
  useEscalationVariants,
  useCreateEscalationVariant,
  useUpdateEscalationVariant,
  useDeleteEscalationVariant,
  useEscalationRules,
  useCreateEscalationRule,
  useUpdateEscalationRule,
  useDeleteEscalationRule,
  useResolveEscalation,
  useP0SlackChannels,
  useCreateP0SlackChannel,
  useUpdateP0SlackChannel,
  useDeleteP0SlackChannel
} from '@/api/hooks';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type EmailPlacement = 'to' | 'cc' | 'bcc';
const EMAIL_PLACEMENTS: ReadonlyArray<[EmailPlacement, string]> = [
  ['to', 'To'],
  ['cc', 'Cc'],
  ['bcc', 'Bcc']
];

type Section = 'variants' | 'channels' | 'rules' | 'tester';

const SECTIONS: ReadonlyArray<[Section, string]> = [
  ['variants', 'Escalation Variants'],
  ['channels', 'Slack Channels'],
  ['rules', 'Contact Rules'],
  ['tester', 'Test Combinations']
];

type Props = {
  readonly verticals: string[];
};

// What a new rule starts with - mirrors EscalationContactRule.DefaultTimeZone. P0 send windows follow
// the UK working day (GMT/BST) by product decision (GT-16894); a rule can still pick another zone.
const DEFAULT_TIME_ZONE = 'Europe/London';
// Every IANA zone the browser knows, offered as type-to-filter suggestions rather than a short fixed
// list. Typing is free-form; the API rejects a zone the server doesn't recognise. UTC is added
// because some engines leave it out of supportedValuesOf.
const TIME_ZONES = ['UTC', ...Intl.supportedValuesOf('timeZone').filter((tz) => tz !== 'UTC')];

export function EscalationAdmin({verticals}: Props) {
  const [section, setSection] = useState<Section>('variants');

  return (
    <div className="flex h-full flex-col gap-4">
      <ToggleGroup
        variant="outline"
        spacing={0}
        size="sm"
        value={[section]}
        onValueChange={(next) => {
          const [first] = next as Section[];
          if (first) {
            setSection(first);
          }
        }}>
        {SECTIONS.map(([key, label]) => (
          <ToggleGroupItem key={key} value={key}>
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {section === 'variants' ? (
        <VariantsManager policyViolations={verticals} />
      ) : section === 'channels' ? (
        <ChannelsManager />
      ) : section === 'rules' ? (
        <RulesManager verticals={verticals} />
      ) : (
        <Tester verticals={verticals} />
      )}
    </div>
  );
}

function ErrorNotice({message}: {readonly message: string}) {
  return (
    <Alert variant="destructive">
      <AlertCircle />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function ListEmpty({title, description}: {readonly title: string; readonly description?: string}) {
  return (
    <Empty className="py-8">
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        {description ? <EmptyDescription>{description}</EmptyDescription> : null}
      </EmptyHeader>
    </Empty>
  );
}

function ListLoading() {
  return (
    <div className="text-muted-foreground flex items-center justify-center gap-2 py-8 text-xs">
      <Spinner /> Loading…
    </div>
  );
}

// A create/edit form sits on the page background as a bordered fieldset, not in a Card: inputs on
// the card tint lose their edge, and a Card is kept here for the read-only lists and results. The
// fieldset is wrapped rather than bordered itself because a browser draws a legend into its
// fieldset's top border.
function FormSection({
  title,
  description,
  children
}: {
  readonly title: string;
  readonly description: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="border-border bg-card rounded-lg border p-4">
      <FieldSet>
        <FieldLegend>{title}</FieldLegend>
        <FieldDescription>{description}</FieldDescription>
        {children}
      </FieldSet>
    </section>
  );
}

/// The directory of Slack channels (each backed by an incoming webhook) a contact rule can target -
/// named so a rule shows "#trust-and-safety-p0" instead of an opaque hooks.slack.com URL. Modeled on
/// VariantsManager/VariantRow below, minus the policy-violation scoping (a channel isn't scoped to one).
function ChannelsManager() {
  const {data: channels = [], isLoading} = useP0SlackChannels();
  const createChannel = useCreateP0SlackChannel();
  const [name, setName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (name.trim() === '' || webhookUrl.trim() === '') {
      return;
    }

    setError(null);
    try {
      await createChannel.mutateAsync({name: name.trim(), webhook_url: webhookUrl.trim()});
      setName('');
      setWebhookUrl('');
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to create Slack channel.'));
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      <FormSection
        title="New Slack channel"
        description="A named incoming webhook that contact rules can send P0 alerts to, alongside or instead of email.">
        <div className="flex flex-wrap items-end gap-3">
          <Field className="w-56">
            <FieldLabel htmlFor="slack-channel-name">Channel name</FieldLabel>
            <Input
              id="slack-channel-name"
              value={name}
              placeholder="e.g. trust-and-safety-p0"
              aria-label="Slack channel name"
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </Field>
          <Field className="min-w-64 flex-1">
            <FieldLabel htmlFor="slack-channel-webhook">Incoming webhook URL</FieldLabel>
            <Input
              id="slack-channel-webhook"
              value={webhookUrl}
              placeholder="https://hooks.slack.com/services/…"
              aria-label="Slack incoming webhook URL"
              onChange={(event) => {
                setWebhookUrl(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void submit();
                }
              }}
            />
          </Field>
          <Button
            size="sm"
            disabled={createChannel.isPending || name.trim() === '' || webhookUrl.trim() === ''}
            onClick={submit}>
            {createChannel.isPending ? <Spinner /> : <Plus />}
            Add channel
          </Button>
        </div>
        {error ? <ErrorNotice message={error} /> : null}
      </FormSection>

      <Card className="gap-0 overflow-hidden p-0">
        <div className="divide-border divide-y">
          {isLoading ? (
            <ListLoading />
          ) : channels.length === 0 ? (
            <ListEmpty title="No Slack channels configured yet" />
          ) : (
            channels.map((c) => <ChannelRow key={c.id} channel={c} />)
          )}
        </div>
      </Card>
    </div>
  );
}

function ChannelRow({channel}: {readonly channel: P0SlackChannel}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(channel.name);
  const [error, setError] = useState<string | null>(null);
  const updateChannel = useUpdateP0SlackChannel();
  const deleteChannel = useDeleteP0SlackChannel();
  const {confirm, confirmDialog} = useConfirm();

  const save = async () => {
    if (name.trim() === '' || name.trim() === channel.name) {
      setEditing(false);
      setName(channel.name);
      return;
    }

    setError(null);
    try {
      await updateChannel.mutateAsync({id: channel.id, body: {name: name.trim()}});
      setEditing(false);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to rename Slack channel.'));
    }
  };

  const toggleEnabled = async () => {
    setError(null);
    try {
      await updateChannel.mutateAsync({id: channel.id, body: {enabled: !channel.enabled}});
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to update Slack channel.'));
    }
  };

  const remove = async () => {
    if (
      !(await confirm({
        title: `Delete the "${channel.name}" Slack channel?`,
        description: 'Any contact rules targeting it will stop delivering there.'
      }))
    ) {
      return;
    }

    setError(null);
    try {
      await deleteChannel.mutateAsync(channel.id);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to delete Slack channel.'));
    }
  };

  return (
    <div className="flex flex-col gap-2 px-4 py-2">
      <div className="hover:bg-card flex items-center justify-between gap-3 transition-colors">
        {editing ? (
          <div className="flex flex-1 items-center gap-2">
            <Input
              autoFocus
              value={name}
              aria-label={`Rename ${channel.name}`}
              className="flex-1"
              onChange={(event) => {
                setName(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void save();
                }

                if (event.key === 'Escape') {
                  setEditing(false);
                  setName(channel.name);
                }
              }}
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Save ${channel.name}`}
              disabled={updateChannel.isPending}
              onClick={save}>
              {updateChannel.isPending ? <Spinner /> : <Check />}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Cancel rename"
              onClick={() => {
                setEditing(false);
                setName(channel.name);
              }}>
              <X />
            </Button>
          </div>
        ) : (
          <>
            <span className="text-foreground flex items-center gap-2 text-xs font-medium">
              #{channel.name}
              {channel.enabled ? null : <Badge variant="outline">Disabled</Badge>}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={updateChannel.isPending}
                onClick={() => void toggleEnabled()}>
                {channel.enabled ? 'Disable' : 'Enable'}
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Edit ${channel.name}`}
                onClick={() => {
                  setEditing(true);
                }}>
                <Pencil />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Delete ${channel.name}`}
                className="hover:text-destructive-ink"
                disabled={deleteChannel.isPending}
                onClick={() => void remove()}>
                <Trash2 />
              </Button>
            </div>
          </>
        )}
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      {confirmDialog}
    </div>
  );
}

function VariantsManager({policyViolations}: {readonly policyViolations: string[]}) {
  const [policyViolation, setPolicyViolation] = useState(policyViolations[0] ?? '');
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const {data: scopedVariants = [], isLoading} = useEscalationVariants(
    policyViolation || undefined
  );
  const {data: allVariants = []} = useEscalationVariants();
  const createVariant = useCreateEscalationVariant();

  // A search query searches every policy violation, not just the one selected for adding a new
  // variant to - otherwise finding a variant means first guessing its policy violation from the dropdown.
  const trimmedSearch = search.trim().toLowerCase();
  const variants = trimmedSearch
    ? allVariants.filter(
        (v) =>
          v.name.toLowerCase().includes(trimmedSearch) ||
          v.policy_violation.toLowerCase().includes(trimmedSearch)
      )
    : scopedVariants;

  const submit = async () => {
    if (name.trim() === '' || policyViolation === '') {
      return;
    }

    setError(null);
    try {
      await createVariant.mutateAsync({policy_violation: policyViolation, name: name.trim()});
      setName('');
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to create escalation variant.'));
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      <FormSection
        title="New escalation variant"
        description="Variants qualify a policy violation so contact rules can target a narrower case.">
        <div className="flex flex-wrap items-end gap-3">
          <Field className="w-56">
            <FieldLabel htmlFor="escalation-variant-policy-violation">Policy violation</FieldLabel>
            <NativeSelect
              wrapperClassName="w-full"
              id="escalation-variant-policy-violation"
              value={policyViolation}
              aria-label="Policy Violation to add the new variant to"
              onChange={(event) => {
                setPolicyViolation(event.target.value);
              }}>
              {policyViolations.map((v) => (
                <NativeSelectOption key={v} value={v}>
                  {v}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field className="min-w-48 flex-1">
            <FieldLabel htmlFor="escalation-variant-name">Variant name</FieldLabel>
            <Input
              id="escalation-variant-name"
              value={name}
              placeholder="New variant name…"
              aria-label="New variant name"
              onChange={(event) => {
                setName(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void submit();
                }
              }}
            />
          </Field>
          <Button
            size="sm"
            disabled={createVariant.isPending || name.trim() === '' || policyViolation === ''}
            onClick={submit}>
            {createVariant.isPending ? <Spinner /> : <Plus />}
            Add variant
          </Button>
        </div>
        {error ? <ErrorNotice message={error} /> : null}
      </FormSection>

      <InputGroup>
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          value={search}
          placeholder="Search variants across all policy violations…"
          aria-label="Search variants across all policy violations"
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </InputGroup>

      <Card className="gap-0 overflow-hidden p-0">
        <div className="divide-border divide-y">
          {isLoading && !trimmedSearch ? (
            <ListLoading />
          ) : variants.length === 0 ? (
            <ListEmpty
              title={
                trimmedSearch
                  ? 'No escalation variants match your search'
                  : 'No escalation variants for this policy violation yet'
              }
            />
          ) : (
            variants.map((v) => (
              <VariantRow key={v.id} variant={v} showPolicyViolation={Boolean(trimmedSearch)} />
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

function VariantRow({
  variant,
  showPolicyViolation = false
}: {
  readonly variant: {id: string; policy_violation: string; name: string};
  readonly showPolicyViolation?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(variant.name);
  const [error, setError] = useState<string | null>(null);
  const updateVariant = useUpdateEscalationVariant();
  const deleteVariant = useDeleteEscalationVariant();
  const {confirm, confirmDialog} = useConfirm();

  const save = async () => {
    if (name.trim() === '' || name.trim() === variant.name) {
      setEditing(false);
      setName(variant.name);
      return;
    }

    setError(null);
    try {
      await updateVariant.mutateAsync({id: variant.id, body: {name: name.trim()}});
      setEditing(false);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to rename escalation variant.'));
    }
  };

  const remove = async () => {
    if (
      !(await confirm({
        title: `Delete the "${variant.name}" variant?`,
        description: 'Any contact rules scoped to it will stop matching.'
      }))
    ) {
      return;
    }

    setError(null);
    try {
      await deleteVariant.mutateAsync(variant.id);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to delete escalation variant.'));
    }
  };

  return (
    <div className="flex flex-col gap-2 px-4 py-2">
      <div className="hover:bg-card flex items-center justify-between gap-3 transition-colors">
        {editing ? (
          <div className="flex flex-1 items-center gap-2">
            <Input
              autoFocus
              value={name}
              aria-label={`Rename ${variant.name}`}
              className="flex-1"
              onChange={(event) => {
                setName(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void save();
                }

                if (event.key === 'Escape') {
                  setEditing(false);
                  setName(variant.name);
                }
              }}
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Save ${variant.name}`}
              disabled={updateVariant.isPending}
              onClick={save}>
              {updateVariant.isPending ? <Spinner /> : <Check />}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Cancel rename"
              onClick={() => {
                setEditing(false);
                setName(variant.name);
              }}>
              <X />
            </Button>
          </div>
        ) : (
          <>
            <span className="text-foreground flex items-center gap-2 text-xs font-medium">
              {variant.name}
              {showPolicyViolation ? (
                <Badge variant="outline">{variant.policy_violation}</Badge>
              ) : null}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Edit ${variant.name}`}
                onClick={() => {
                  setEditing(true);
                }}>
                <Pencil />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Delete ${variant.name}`}
                className="hover:text-destructive-ink"
                disabled={deleteVariant.isPending}
                onClick={() => void remove()}>
                <Trash2 />
              </Button>
            </div>
          </>
        )}
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      {confirmDialog}
    </div>
  );
}

function RulesManager({verticals}: {readonly verticals: string[]}) {
  const {data: rules = [], isLoading} = useEscalationRules();
  const createRule = useCreateEscalationRule();
  const updateRule = useUpdateEscalationRule();
  const deleteRule = useDeleteEscalationRule();
  const {confirm, confirmDialog} = useConfirm();
  const {data: allVariants = []} = useEscalationVariants();
  const {data: allChannels = []} = useP0SlackChannels();

  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [policyViolation, setPolicyViolation] = useState('');
  const [variant, setVariant] = useState('');
  const [days, setDays] = useState<Set<string>>(new Set());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timeZone, setTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [emailFields, setEmailFields] = useState<Record<EmailPlacement, string>>({
    to: '',
    cc: '',
    bcc: ''
  });
  const [slackChannelIds, setSlackChannelIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const setEmailField = (placement: EmailPlacement, value: string) => {
    setEmailFields((previous) => ({...previous, [placement]: value}));
  };

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setPolicyViolation('');
    setVariant('');
    setDays(new Set());
    setStartTime('');
    setEndTime('');
    setTimeZone(DEFAULT_TIME_ZONE);
    setEmailFields({to: '', cc: '', bcc: ''});
    setSlackChannelIds(new Set());
  };

  const startEdit = (rule: EscalationContactRule) => {
    setEditingId(rule.id);
    setName(rule.name);
    setPolicyViolation(rule.policy_violation ?? '');
    setVariant(rule.escalation_variant ?? '');
    setDays(new Set(rule.days_of_week ? rule.days_of_week.split(',') : []));
    setStartTime(rule.start_time ?? '');
    setEndTime(rule.end_time ?? '');
    setTimeZone(rule.time_zone);
    setEmailFields({
      to: rule.to_emails.join(', '),
      cc: rule.cc_emails.join(', '),
      bcc: rule.bcc_emails.join(', ')
    });
    setSlackChannelIds(new Set(rule.slack_channel_ids));
    setError(null);
  };

  const parseEmails = (value: string) =>
    value
      .split(/[\s,]+/)
      .map((e) => e.trim())
      .filter((e) => e !== '');

  const submit = async () => {
    const emails: Record<EmailPlacement, string[]> = {
      to: parseEmails(emailFields.to),
      cc: parseEmails(emailFields.cc),
      bcc: parseEmails(emailFields.bcc)
    };
    if (
      name.trim() === '' ||
      (emails.to.length === 0 &&
        emails.cc.length === 0 &&
        emails.bcc.length === 0 &&
        slackChannelIds.size === 0)
    ) {
      return;
    }

    // Entering only one of start/end time is ambiguous - block the submit rather than silently
    // discarding both (the old `clear_time_window: !startTime || !endTime` behavior).
    if (Boolean(startTime) !== Boolean(endTime)) {
      setError('Enter both a start time and an end time, or leave both empty.');
      return;
    }

    setError(null);
    try {
      if (editingId) {
        await updateRule.mutateAsync({
          id: editingId,
          body: {
            name: name.trim(),
            policy_violation: policyViolation || null,
            clear_policy_violation: !policyViolation,
            escalation_variant: variant || null,
            clear_escalation_variant: !variant,
            days_of_week: days.size > 0 ? [...days].join(',') : null,
            clear_days_of_week: days.size === 0,
            ...(startTime && endTime ? {start_time: startTime, end_time: endTime} : {}),
            clear_time_window: !startTime && !endTime,
            time_zone: timeZone.trim() || DEFAULT_TIME_ZONE,
            to_emails: emails.to,
            cc_emails: emails.cc,
            bcc_emails: emails.bcc,
            slack_channel_ids: [...slackChannelIds]
          }
        });
      } else {
        await createRule.mutateAsync({
          name: name.trim(),
          policy_violation: policyViolation || null,
          escalation_variant: variant || null,
          days_of_week: days.size > 0 ? [...days].join(',') : null,
          start_time: startTime || null,
          end_time: endTime || null,
          time_zone: timeZone.trim(),
          to_emails: emails.to,
          cc_emails: emails.cc,
          bcc_emails: emails.bcc,
          slack_channel_ids: [...slackChannelIds]
        });
      }

      resetForm();
    } catch (error_) {
      setError(
        getApiErrorMessage(error_, `Failed to ${editingId ? 'update' : 'create'} contact rule.`)
      );
    }
  };

  const removeRule = async (rule: EscalationContactRule) => {
    if (
      !(await confirm({
        title: `Delete the "${rule.name}" contact rule?`,
        description: 'This cannot be undone.'
      }))
    ) {
      return;
    }

    setError(null);
    try {
      await deleteRule.mutateAsync(rule.id);
      if (editingId === rule.id) {
        resetForm();
      }
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to delete contact rule.'));
    }
  };

  const variantOptions = allVariants.filter(
    (v) => !policyViolation || v.policy_violation === policyViolation
  );

  const trimmedSearch = search.trim().toLowerCase();
  const filteredRules = useMemo(() => {
    if (!trimmedSearch) {
      return rules;
    }

    return rules.filter((rule) => {
      const channelNames = rule.slack_channel_ids.map(
        (id) => allChannels.find((c) => c.id === id)?.name
      );
      const haystack = [
        rule.name,
        rule.policy_violation,
        rule.escalation_variant,
        ...rule.to_emails,
        ...rule.cc_emails,
        ...rule.bcc_emails,
        ...channelNames
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(trimmedSearch);
    });
  }, [rules, trimmedSearch, allChannels]);

  const saving = createRule.isPending || updateRule.isPending;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <FormSection
        title={editingId ? 'Edit contact rule' : 'New contact rule'}
        description="A rule adds its recipients to every P0 alert that matches its policy violation, variant, day and time window. Leave a filter empty to match everything.">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor="contact-rule-name">Rule name</FieldLabel>
              <Input
                id="contact-rule-name"
                value={name}
                placeholder="Rule name"
                aria-label="Rule name"
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-rule-time-zone">Time zone</FieldLabel>
              <Input
                id="contact-rule-time-zone"
                value={timeZone}
                list="escalation-rule-time-zones"
                placeholder="Time zone, e.g. Europe/London"
                aria-label="Time zone"
                onChange={(event) => {
                  setTimeZone(event.target.value);
                }}
              />
              <datalist id="escalation-rule-time-zones">
                {TIME_ZONES.map((tz) => (
                  <option key={tz} value={tz} />
                ))}
              </datalist>
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-rule-policy-violation">Policy violation</FieldLabel>
              <NativeSelect
                wrapperClassName="w-full"
                id="contact-rule-policy-violation"
                value={policyViolation}
                aria-label="Policy Violation"
                onChange={(event) => {
                  setPolicyViolation(event.target.value);
                  setVariant('');
                }}>
                <NativeSelectOption value="">Any policy violation</NativeSelectOption>
                {verticals.map((v) => (
                  <NativeSelectOption key={v} value={v}>
                    {v}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-rule-variant">Escalation variant</FieldLabel>
              <NativeSelect
                wrapperClassName="w-full"
                id="contact-rule-variant"
                value={variant}
                aria-label="Escalation variant"
                onChange={(event) => {
                  setVariant(event.target.value);
                }}>
                <NativeSelectOption value="">Any escalation variant</NativeSelectOption>
                {variantOptions.map((v) => (
                  <NativeSelectOption key={v.id} value={v.name}>
                    {v.name} ({v.policy_violation})
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-rule-start-time">Start time</FieldLabel>
              <Input
                id="contact-rule-start-time"
                type="time"
                value={startTime}
                aria-label="Start time"
                onChange={(event) => {
                  setStartTime(event.target.value);
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-rule-end-time">End time</FieldLabel>
              <Input
                id="contact-rule-end-time"
                type="time"
                value={endTime}
                aria-label="End time"
                onChange={(event) => {
                  setEndTime(event.target.value);
                }}
              />
            </Field>
          </div>

          <Field>
            <FieldLabel>Days of week</FieldLabel>
            <div className="flex flex-wrap items-center gap-3">
              <ToggleGroup
                multiple
                variant="outline"
                spacing={0}
                size="sm"
                aria-label="Days of week"
                value={[...days]}
                onValueChange={(next) => {
                  setDays(new Set(next));
                }}>
                {DAYS.map((day) => (
                  <ToggleGroupItem key={day} value={day}>
                    {day}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              {days.size === 0 ? (
                <span className="text-muted-foreground text-xs">No days selected = every day</span>
              ) : null}
            </div>
          </Field>

          <div className="grid grid-cols-3 gap-3">
            {EMAIL_PLACEMENTS.map(([key, label]) => (
              <Field key={key}>
                <FieldLabel htmlFor={`contact-rule-${key}-emails`}>{label} emails</FieldLabel>
                <Input
                  id={`contact-rule-${key}-emails`}
                  value={emailFields[key]}
                  placeholder={`${label} emails (comma or space separated)`}
                  aria-label={`${label} emails (comma or space separated)`}
                  onChange={(event) => {
                    setEmailField(key, event.target.value);
                  }}
                />
              </Field>
            ))}
          </div>

          <Field>
            <FieldLabel>Slack channels</FieldLabel>
            {allChannels.length === 0 ? (
              <span className="text-muted-foreground text-xs">
                No Slack channels configured yet - add one under the Slack Channels tab
              </span>
            ) : (
              <ToggleGroup
                multiple
                variant="outline"
                spacing={0}
                size="sm"
                aria-label="Slack channels"
                className="flex-wrap"
                value={[...slackChannelIds]}
                onValueChange={(next) => {
                  setSlackChannelIds(new Set(next));
                }}>
                {allChannels.map((c) => (
                  <ToggleGroupItem
                    key={c.id}
                    value={c.id}
                    disabled={!c.enabled && !slackChannelIds.has(c.id)}
                    title={c.enabled ? undefined : 'Disabled channel'}>
                    #{c.name}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            )}
          </Field>

          {error ? <ErrorNotice message={error} /> : null}
          <div className="flex items-center gap-2">
            {editingId ? (
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X /> Cancel
              </Button>
            ) : null}
            <Button size="sm" disabled={saving} onClick={submit}>
              {saving ? <Spinner /> : editingId ? <Check /> : <Plus />}
              {editingId ? 'Save Changes' : 'Add Rule'}
            </Button>
          </div>
        </div>
      </FormSection>

      <InputGroup>
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          value={search}
          placeholder="Search by name, policy violation, variant, email, or Slack channel…"
          aria-label="Search contact rules by name, policy violation, variant, email, or Slack channel"
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </InputGroup>

      <Card className="gap-0 overflow-hidden p-0">
        {isLoading ? (
          <ListLoading />
        ) : filteredRules.length === 0 ? (
          <ListEmpty
            title={
              trimmedSearch ? 'No contact rules match your search' : 'No contact rules configured'
            }
          />
        ) : (
          <div className="divide-border divide-y">
            {filteredRules.map((rule) => (
              <div
                key={rule.id}
                className="hover:bg-card flex items-start justify-between gap-3 px-4 py-3 transition-colors">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <p className="text-foreground text-xs font-medium">{rule.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {rule.policy_violation ?? 'Any policy violation'} ·{' '}
                    {rule.escalation_variant ?? 'Any variant'} · {rule.days_of_week ?? 'Every day'}{' '}
                    ·{' '}
                    {rule.start_time && rule.end_time
                      ? `${rule.start_time}–${rule.end_time} ${rule.time_zone}`
                      : 'All day'}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {EMAIL_PLACEMENTS.flatMap(([key, label]) =>
                      ({to: rule.to_emails, cc: rule.cc_emails, bcc: rule.bcc_emails})[key].map(
                        (email) => (
                          <Badge key={`${key}-${email}`} variant="outline">
                            <span className="text-muted-foreground">{label}:</span> {email}
                          </Badge>
                        )
                      )
                    )}
                    {rule.slack_channel_ids.map((channelId) => (
                      <Badge key={channelId} variant="outline">
                        #{allChannels.find((c) => c.id === channelId)?.name ?? channelId}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Edit ${rule.name}`}
                    onClick={() => {
                      startEdit(rule);
                    }}>
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete ${rule.name}`}
                    className="hover:text-destructive-ink"
                    disabled={deleteRule.isPending}
                    onClick={() => void removeRule(rule)}>
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {confirmDialog}
    </div>
  );
}

function Tester({verticals}: {readonly verticals: string[]}) {
  const [selectedPolicyViolations, setSelectedPolicyViolations] = useState<Set<string>>(new Set());
  const [variants, setVariants] = useState<Set<string>>(new Set());
  const [at, setAt] = useState('');
  const {data: allVariants = []} = useEscalationVariants();
  const resolveEscalation = useResolveEscalation();
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EscalationResolveResult | null>(null);

  const setPolicyViolations = (next: string[]) => {
    setSelectedPolicyViolations(new Set(next));
    // Variant choices only make sense scoped to the currently-selected policy violations - clear
    // them rather than silently testing a variant that no longer belongs to any selected policy
    // violation.
    setVariants(new Set());
  };

  const variantOptions =
    selectedPolicyViolations.size === 0
      ? allVariants
      : allVariants.filter((v) => selectedPolicyViolations.has(v.policy_violation));

  const run = async () => {
    setError(null);
    try {
      const at_ = at ? new Date(at).toISOString() : undefined;

      // The resolve endpoint only takes one policy violation at a time - a multi-policy-violation
      // test (e.g. a P0 bundle spanning "Hate" and "Violent Extremism") fans out one call per
      // selected policy violation and unions the results, same as how a real "mixed"-policy-violation
      // P0 alert combines coverage. Each per-policy-violation call is scoped down to only the
      // variants that actually belong to that policy violation - a variant selected for a different
      // policy violation could never occur on a real single-policy-violation P0 alert, so passing the
      // whole cross-policy-violation selection through would let the tester report a recipient match
      // for a policy-violation+variant combination that can't happen.
      const targets =
        selectedPolicyViolations.size > 0 ? [...selectedPolicyViolations] : [undefined];
      const results = await Promise.all(
        targets.map(async (v) => {
          const variantsForTarget = v
            ? [...variants].filter((name) =>
                allVariants.some((av) => av.name === name && av.policy_violation === v)
              )
            : [...variants];
          return resolveEscalation.mutateAsync({
            ...(v ? {policyViolation: v} : {}),
            ...(variantsForTarget.length > 0 ? {escalationVariants: variantsForTarget} : {}),
            ...(at_ ? {at: at_} : {})
          });
        })
      );

      const dedupeLower = (values: string[]) =>
        [...new Set(values.map((e) => e.toLowerCase()))].sort();
      const toEmails = dedupeLower(results.flatMap((r) => r.to_emails));
      // Each per-policy-violation call already applies To > Cc > Bcc precedence internally, but a
      // real alert spanning multiple policy violations resolves all of them in a single backend
      // call - re-apply the same precedence here across the merged calls, or an address that would
      // collapse to one placement in reality could show up under two placements in this preview.
      const ccEmails = dedupeLower(results.flatMap((r) => r.cc_emails)).filter(
        (e) => !toEmails.includes(e)
      );
      const bccEmails = dedupeLower(results.flatMap((r) => r.bcc_emails)).filter(
        (e) => !toEmails.includes(e) && !ccEmails.includes(e)
      );
      const slackChannels = dedupeLower(results.flatMap((r) => r.slack_channels));
      const matchedRules = [
        ...new Map(results.flatMap((r) => r.matched_rules).map((m) => [m.id, m])).values()
      ];
      setResult({
        to_emails: toEmails,
        cc_emails: ccEmails,
        bcc_emails: bccEmails,
        slack_channels: slackChannels,
        matched_rules: matchedRules
      });
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to resolve.'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <FormSection
        title="Test a combination"
        description="Resolve who a P0 alert would reach for a given policy violation, variant and time, without sending anything.">
        <Field>
          <FieldLabel>Policy violations (leave empty for wildcard-only rules)</FieldLabel>
          <ToggleGroup
            multiple
            variant="outline"
            spacing={0}
            size="sm"
            aria-label="Policy violations"
            className="flex-wrap"
            value={[...selectedPolicyViolations]}
            onValueChange={(next) => {
              setPolicyViolations(next);
            }}>
            {verticals.map((v) => (
              <ToggleGroupItem key={v} value={v}>
                {v}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </Field>
        <Field className="w-72">
          <FieldLabel htmlFor="escalation-test-at">Resolve as of</FieldLabel>
          <Input
            id="escalation-test-at"
            type="datetime-local"
            value={at}
            aria-label="Resolve as of"
            onChange={(event) => {
              setAt(event.target.value);
            }}
          />
        </Field>
        <Field>
          <FieldLabel>Escalation variants (leave empty for wildcard-only rules)</FieldLabel>
          {variantOptions.length === 0 ? (
            <span className="text-muted-foreground text-xs">
              No variants for the selected policy violations
            </span>
          ) : (
            <ToggleGroup
              multiple
              variant="outline"
              spacing={0}
              size="sm"
              aria-label="Escalation variants"
              className="flex-wrap"
              value={[...variants]}
              onValueChange={(next) => {
                setVariants(new Set(next));
              }}>
              {variantOptions.map((v) => (
                <ToggleGroupItem key={v.id} value={v.name}>
                  {v.name} ({v.policy_violation})
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
        </Field>

        {error ? <ErrorNotice message={error} /> : null}
        <div>
          <Button size="sm" disabled={resolveEscalation.isPending} onClick={() => void run()}>
            {resolveEscalation.isPending ? <Spinner /> : null}
            Resolve Recipients
          </Button>
        </div>
      </FormSection>

      {result ? (
        <Card>
          <CardHeader>
            <CardTitle>Resolved recipients</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {result.to_emails.length === 0 &&
            result.cc_emails.length === 0 &&
            result.bcc_emails.length === 0 &&
            result.slack_channels.length === 0 ? (
              <p className="text-muted-foreground text-xs">No matching rules — no recipients</p>
            ) : (
              EMAIL_PLACEMENTS.map(([key, label]) => {
                const addresses = {
                  to: result.to_emails,
                  cc: result.cc_emails,
                  bcc: result.bcc_emails
                }[key];
                return addresses.length === 0 ? null : (
                  <div key={key} className="flex flex-wrap items-center gap-1.5">
                    <span className="text-muted-foreground w-8 text-xs font-medium">{label}:</span>
                    {addresses.map((email) => (
                      <Badge key={email} variant="outline">
                        {email}
                      </Badge>
                    ))}
                  </div>
                );
              })
            )}
            {result.slack_channels.length === 0 ? null : (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-muted-foreground w-8 text-xs font-medium">Slack:</span>
                {result.slack_channels.map((channel) => (
                  <Badge key={channel} variant="outline">
                    #{channel}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
          <CardHeader>
            <CardTitle>Matched rules</CardTitle>
          </CardHeader>
          <CardContent>
            {result.matched_rules.length === 0 ? (
              <p className="text-muted-foreground text-xs">None</p>
            ) : (
              <ul className="text-foreground list-inside list-disc text-xs">
                {result.matched_rules.map((r) => (
                  <li key={r.id}>{r.name}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
